/**
 * Fixed-timestep clock.
 *
 * Gameplay runs at a fixed 120 Hz regardless of display refresh. Physics,
 * ballistics, AI and cooldowns all advance in `fixedUpdate` so behaviour is
 * frame-rate independent and reproducible. Rendering interpolates between the
 * last two physics states using `alpha`, which is what keeps motion smooth on a
 * 60 Hz display while the simulation ticks at 120.
 */
export const FIXED_HZ = 120;
export const FIXED_DT = 1 / FIXED_HZ;

// If a frame stalls (tab backgrounded, shader compile, GC pause) we must not try
// to catch up tick-for-tick — that produces a death spiral where each frame owes
// more simulation than the last. Clamp and accept a small time debt instead.
const MAX_FRAME_DT = 0.25;
const MAX_STEPS_PER_FRAME = 8;

export class Time {
  constructor() {
    this.elapsed = 0;   // gameplay seconds, affected by scale
    this.raw = 0;       // wall-clock seconds since boot
    this.dt = 0;        // scaled delta for this render frame
    this.rawDt = 0;     // unscaled delta for this render frame
    this.fixed = FIXED_DT;
    this.alpha = 0;     // [0,1) interpolation between physics ticks
    this.scale = 1;     // time dilation — used by the super activation
    this.frame = 0;
    this.tick = 0;      // fixed-step counter

    this._accumulator = 0;
    this._last = 0;
    this._started = false;

    // Smoothed frame time for the perf overlay. Raw per-frame numbers are too
    // noisy to read; a running average makes regressions visible.
    this.smoothedFrameMs = 16.6;
  }

  /**
   * Advance wall clock. Returns the number of fixed steps the caller should run.
   * @param {number} now performance.now() in milliseconds
   */
  beginFrame(now) {
    if (!this._started) {
      this._started = true;
      this._last = now;
      return 0;
    }

    let rawDt = (now - this._last) / 1000;
    this._last = now;
    if (rawDt > MAX_FRAME_DT) rawDt = MAX_FRAME_DT;

    this.rawDt = rawDt;
    this.raw += rawDt;
    this.dt = rawDt * this.scale;
    this.elapsed += this.dt;
    this.frame++;

    this.smoothedFrameMs += ((rawDt * 1000) - this.smoothedFrameMs) * 0.1;

    this._accumulator += this.dt;
    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      this._accumulator -= FIXED_DT;
      steps++;
    }

    // Dropped simulation time is discarded rather than banked, for the reason
    // described above. Banking it guarantees the next frame is even later.
    if (steps === MAX_STEPS_PER_FRAME) this._accumulator = 0;

    this.alpha = this._accumulator / FIXED_DT;
    return steps;
  }

  /** Called by the loop after each fixed step so systems can read the counter. */
  advanceTick() {
    this.tick++;
  }

  get fps() {
    return 1000 / Math.max(0.001, this.smoothedFrameMs);
  }
}
